import { apiGateway } from '@va/api-client';
import { post } from '@va/http-client';
import { getConfig } from '@va/standalone/shared/api-client';
import { ApiOperators, Where } from '@va/types/filters';
import { RawTimeseriesDataset, RawTimeseriesResponse } from '@va/types/time-series';
import { calculatePercentFromTotal, toQueryString } from '@va/util/helpers';
import { isArray } from 'lodash';

export type FetchFuncParams = {
  websiteId: string;
  query: Record<string, any>;
  body: { where?: Where };
  signal?: AbortSignal;
};

export async function fetchVisits({ websiteId, query, body, signal }: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/visits/graph`, query, body, { signal });
}

export const fetchPercentOfTotalPageViewsGraph = async (params: FetchFuncParams): Promise<RawTimeseriesResponse> => {
  const totalP = fetchVisits({ websiteId: params.websiteId, signal: params.signal, body: {}, query: params.query });
  const filteredP = fetchVisits(params);

  const [totalRes, filteredRes] = await Promise.all([totalP, filteredP]);

  const totalDataset = totalRes.payload.datasets[0];
  const filteredDataset = filteredRes.payload.datasets[0];

  if (!totalDataset || !filteredDataset) {
    throw new Error('invalid datasets');
  }

  const dataset: RawTimeseriesDataset = { data: [], label: 'Percent Of Total Page Views' };

  totalDataset.data.forEach((totalVal, index) => {
    const filteredVal = filteredDataset.data[index];
    dataset.data[index] = calculatePercentFromTotal(filteredVal, totalVal, { round: false });
  });

  return {
    payload: { datasets: [dataset], labels: totalRes.payload.labels },
  };
};

export function fetchDirectVisits({ websiteId, query, body, signal }: FetchFuncParams) {
  const where: Where = {
    and: [{ member: 'trafficChannel', operator: ApiOperators.in, values: [{ type: 'direct', category: [] }] }],
  };
  if (body.where?.and && isArray(body.where.and)) {
    body.where.and.forEach((condition) => {
      if (condition.member === 'trafficChannel') {
        return;
      }
      where.and.push(condition);
    });
  }
  return fetchVisits({ websiteId, query, signal, body: { ...body, where: where } });
}

/**
 * Retrieves new, returning and overall visitors
 * @param websiteId
 * @param query
 * @param body
 * @param signal
 */
export async function fetchVisitors({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/visitors/all/graph`, query, body, { signal });
}

export async function fetchAveragePageViewDuration({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/stats/average-page-view-duration/graph`, query, body, { signal });
}

export async function fetchBounceRateGraph({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/stats/bounce-rate/graph`, query, body, { signal });
}

export async function fetchSessions({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/sessions/all/graph`, query, body, { signal });
}

export async function fetchEcomSessions({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/ecom/graph/sessions`, query, body, { signal });
}

export async function fetchConvertingVisitors({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  return post(`/v2/websites/${websiteId}/visitors/converting/graph`, query, body, { signal });
}

export async function fetchConvertedSessions(params: FetchFuncParams): Promise<RawTimeseriesResponse> {
  const res = await fetchConversionEvents(params);
  res.payload.datasets = res.payload.datasets.filter((item) => item.label === 'Converted Sessions');
  return res;
}

export async function fetchConversionEvents({
  websiteId,
  query,
  body,
  signal,
}: FetchFuncParams): Promise<RawTimeseriesResponse> {
  const res = await apiGateway.post<RawTimeseriesResponse>(
    `/v2/websites/${websiteId}/conversion-events/stats/graph?${toQueryString(query)}`,
    body,
    {
      ...getConfig(),
      signal,
    },
  );

  const customOrder = ['Converted Sessions', 'Conversion Count', 'Conversion Value', 'Conversion Rate (%)'];

  res.payload.datasets.sort((a, b) => customOrder.indexOf(a.label) - customOrder.indexOf(b.label));

  return res;
}
